import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Navbar from "../NavBar";
import Footer from "../Footer";

const weakSystemSignals = [
  "Workflows are fragmented",
  "Accountability is unclear",
  "Reporting is unreliable",
  "Data cannot be trusted",
];

const systemCapabilities = [
  "Reflect real-world workflows",
  "Enforce validation and accountability",
  "Remain auditable and traceable",
  "Support multiple stakeholders",
  "Operate reliably under real conditions",
];

const workingStructure = [
  "How work actually moves",
  "Where control must exist",
  "Who needs visibility",
  "What decisions must be supported",
];

const approachLayers = [
  {
    title: "Structure",
    body: "Defining workflows and system logic",
  },
  {
    title: "Governance",
    body: "Embedding control, validation, and accountability",
  },
  {
    title: "Execution",
    body: "Delivering systems that perform in live environments",
  },
];

const focusAreas = [
  "Governance and institutional platforms",
  "Workflow and validation systems",
  "Compliance and audit systems",
  "Market infrastructure platforms",
  "Dashboards and reporting systems",
];

const clientNeeds = [
  "Coordinate multiple stakeholders",
  "Support compliance and oversight",
  "Handle real transactions and decisions",
  "Produce reliable, decision-ready data",
];

const values = [
  { title: "Clarity", body: "Systems must be understandable" },
  { title: "Precision", body: "Structure must be deliberate" },
  { title: "Control", body: "Outcomes must be governed" },
  { title: "Execution", body: "Systems must work in practice" },
];

const outcomes = [
  ["Fragmented processes", "structured workflows"],
  ["Weak oversight", "controlled execution"],
  ["Scattered data", "reliable reporting"],
];

export default function About() {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(hash.replace("#", ""));
    if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  }, [hash]);

  return (
    <section className="w-full relative bg-white text-gray-800">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[520px] bg-[#101722] text-white overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{ backgroundImage: "url('/images/sliders/_dashboard.jpg')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#101722] via-[#101722]/90 to-[#101722]/45" />
        <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-20 py-28">
          <p className="text-sm uppercase tracking-[0.2em] text-[#44c4bd] font-semibold mb-5">
            About GEM Consulting
          </p>
          <h1 className="text-4xl md:text-6xl font-bold leading-tight max-w-4xl">
            We Design Systems That Institutions Can Trust
          </h1>
          <p className="mt-6 max-w-3xl text-lg text-white/75 leading-relaxed">
            GEM Consulting is a systems-focused advisory and delivery firm helping institutions
            bring structure, control, and clarity to complex operations.
          </p>
          <p className="mt-4 max-w-3xl text-lg text-white/75 leading-relaxed">
            We design governance-grade digital systems where workflow, accountability, and
            execution must align.
          </p>
        </div>
      </section>
      <section className="bg-gray-100 py-16">
      <div className="max-w-5xl mx-auto px-6">
        <div className="flex items-start gap-6">
          
          {/* Red vertical line */}
          <div className="w-[10px] bg-red-600 self-stretch" />

          {/* Text */}
          <p className="text-[28px] md:text-[44px] font-medium text-gray-900 leading-[1.2] tracking-tight">
            At Gem, We design governance-grade digital systems where workflow, accountability, and execution must align.
          </p>

        </div>
      </div>
    </section>

      {/* What We Stand For */}
      <section id="stand-for" className="py-16 px-6 lg:px-20 border-b border-gray-100 scroll-mt-20">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-12">
          <div>
            <p className="text-sm uppercase tracking-[0.18em] text-[#007681] font-bold mb-3">
              What We Stand For
            </p>
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Most institutions don't fail because of technology.
            </h2>
            <p className="text-gray-600 leading-relaxed text-lg mb-4">
              They fail because the system around the technology is weak.
            </p>
            <p className="text-gray-500 leading-relaxed">
              Over time, this creates operational risk and weakens execution. We exist to fix that.
            </p>
          </div>
          <div className="bg-[#f5f7fa] border-l-4 border-red-600 p-6">
            <ul className="space-y-4">
              {weakSystemSignals.map((item) => (
                <li key={item} className="flex items-start gap-3 text-gray-800 font-semibold">
                  <span className="text-red-600 mt-0.5 shrink-0">✕</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <ContentBand
        id="what-we-do"
        eyebrow="What We Do"
        title="We design and deliver systems built to hold."
        body="These are not generic platforms. They are systems built to hold."
        items={systemCapabilities}
        bg="bg-white"
      />

      {/* How We Work */}
      <section className="bg-[#f5f7fa] py-16 px-6 lg:px-20 border-t border-gray-100">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-12">
          <div>
            <p className="text-sm uppercase tracking-[0.18em] text-[#007681] font-bold mb-3">
              How We Work
            </p>
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">We start with structure.</h2>
            <p className="text-gray-600 leading-relaxed text-lg mb-2">
              Before building anything, we define:
            </p>
            <p className="text-gray-500 leading-relaxed mt-4">
              Then we design and deliver systems that reflect that reality.
            </p>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4 content-start">
            {workingStructure.map((item) => (
              <li key={item} className="bg-white border border-gray-100 shadow-sm p-5 font-semibold text-gray-800">
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Our Approach */}
      <section id="approach" className="bg-[#101722] text-white py-16 px-6 lg:px-20 scroll-mt-20">
        <div className="max-w-6xl mx-auto">
          <p className="text-sm uppercase tracking-[0.18em] text-[#44c4bd] font-bold mb-3">
            Our Approach
          </p>
          <h2 className="text-3xl lg:text-4xl font-bold mb-3">
            A system is only useful if it works under real conditions.
          </h2>
          <p className="text-white/60 mb-10">
            Our work is guided by that principle. We focus on three layers:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {approachLayers.map((layer, i) => (
              <div key={layer.title} className="border border-white/15 p-6">
                <p className="text-[#44c4bd] font-bold text-3xl mb-4">{i + 1}</p>
                <h3 className="text-xl font-bold mb-3">{layer.title}</h3>
                <p className="text-white/70 leading-relaxed">{layer.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <Link
              to="/framework"
              className="text-sm text-[#44c4bd] hover:text-white transition-colors font-semibold flex items-center gap-1.5"
            >
              → Read the Systems Execution Framework
            </Link>
          </div>
        </div>
      </section>

      {/* Where We Focus */}
      <ContentBand
        id="focus"
        eyebrow="Where We Focus"
        title="Systems that require precision and trust."
        items={focusAreas}
        bg="bg-white"
      />

      {/* Why Clients Work With Us */}
      <section className="bg-[#f5f7fa] py-16 px-6 lg:px-20 border-t border-gray-100">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-12">
          <div>
            <p className="text-sm uppercase tracking-[0.18em] text-[#007681] font-bold mb-3">
              Why Clients Work With Us
            </p>
            <h2 className="text-3xl lg:text-4xl font-bold mb-5">
              Clients come to us when the problem is not just building software.
            </h2>
            <p className="text-gray-600 leading-relaxed text-lg">
              They choose us because we understand the structure behind the system, not just
              the interface.
            </p>
          </div>
          <div>
            <p className="text-gray-500 mb-4">They need systems that:</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {clientNeeds.map((item) => (
                <li key={item} className="bg-white border border-gray-100 shadow-sm p-5 font-semibold text-gray-800">
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* What We Value */}
      <section id="values" className="bg-white py-16 px-6 lg:px-20 border-t border-gray-100 scroll-mt-20">
        <div className="max-w-6xl mx-auto">
          <p className="text-sm uppercase tracking-[0.18em] text-[#007681] font-bold mb-3">
            What We Value
          </p>
          <h2 className="text-3xl lg:text-4xl font-bold mb-3">
            We don't build for appearance.
          </h2>
          <p className="text-gray-500 mb-10 text-lg">We build for reliability.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map((value) => (
              <div key={value.title} className="border-l-4 border-red-600 bg-[#f5f7fa] p-5">
                <h3 className="font-bold text-lg mb-2">{value.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{value.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Outcome */}
      <section className="bg-[#edf3f7] py-16 px-6 lg:px-20">
        <div className="max-w-6xl mx-auto">
          <p className="text-sm uppercase tracking-[0.18em] text-[#007681] font-bold mb-3">
            The Outcome
          </p>
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">
            We help institutions move from disorder to controlled execution.
          </h2>
          <p className="text-gray-500 mb-10">Structured. Controlled. Reliable.</p>
          <div className="space-y-5">
            {outcomes.map(([before, after]) => (
              <div key={before} className="flex flex-col sm:flex-row sm:items-center gap-3 text-lg">
                <span className="text-gray-400 line-through min-w-[220px]">{before}</span>
                <span className="text-red-600 font-bold text-xl">→</span>
                <span className="font-semibold text-gray-900">{after}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </section>
  );
}

function ContentBand({ id, eyebrow, title, body, items, bg }) {
  return (
    <section id={id} className={`${bg} py-16 px-6 lg:px-20 border-t border-gray-100 scroll-mt-20`}>
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-12">
        <div>
          <p className="text-sm uppercase tracking-[0.18em] text-[#007681] font-bold mb-3">
            {eyebrow}
          </p>
          <h2 className="text-3xl lg:text-4xl font-bold mb-5">{title}</h2>
          {body && <p className="text-gray-600 leading-relaxed text-lg">{body}</p>}
        </div>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4 content-start">
          {items.map((item) => (
            <li key={item} className="bg-white border border-gray-100 shadow-sm p-5 font-semibold text-gray-800">
              {item}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
